import { useState, useRef, useEffect, memo } from "react";
import { Link } from "react-router-dom";
import { ComposableMap, Geographies, Geography, ZoomableGroup } from "react-simple-maps";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Heart, Home, ThermometerSun, Plane, Landmark, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { useI18n } from "../i18n";
import { useAuth } from "../auth";
import ListenButton from "./ListenButton";
import ShareBadge from "./ShareBadge";
import LinkIntelligence from "./LinkIntelligence";
import { HIERARCHY_PATHS } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("sp_token")}` });

const GEO_URL = "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

const PREMIUM = {
  India: { domain: "Indias.com", path: "india" },
  "United States of America": { domain: "USAPulse.net", path: "usa" },
};

const slug = (name) => name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const MapChart = memo(({ selected, onSelect, namesRef }) => (
  <ComposableMap projectionConfig={{ scale: 150 }} style={{ width: "100%", height: "auto" }}>
    <ZoomableGroup center={[10, 10]} zoom={1} minZoom={1} maxZoom={5}>
      <Geographies geography={GEO_URL}>
        {({ geographies }) => {
          if (!namesRef.current.length) namesRef.current = geographies.map((g) => g.properties.name);
          return geographies.map((geo) => {
            const name = geo.properties.name;
            const isSel = selected === name;
            const isPremium = !!PREMIUM[name];
            return (
              <Geography
                key={geo.rsmKey}
                geography={geo}
                onClick={() => onSelect(name)}
                className="geo-path"
                fill={isSel ? "#2563EB" : isPremium ? "#93C5FD" : "#CFE3F8"}
                stroke="#FFFFFF"
                strokeWidth={0.5}
                style={{ outline: "none", cursor: "pointer", transition: "fill 0.3s" }}
              />
            );
          });
        }}
      </Geographies>
    </ZoomableGroup>
  </ComposableMap>
));

export default function WorldMap({ searchQuery, searchTick }) {
  const { tr } = useI18n();
  const { user, updatePreferences } = useAuth();
  const [selected, setSelected] = useState("India");
  const [follows, setFollows] = useState([]);
  const [feeds, setFeeds] = useState(null);
  const [feedsLoading, setFeedsLoading] = useState(false);
  const namesRef = useRef([]);

  useEffect(() => {
    if (!user) { setFollows([]); return; }
    const load = () =>
      axios.get(`${API}/follows`, { headers: authHeaders() })
        .then((r) => setFollows(r.data.map((f) => f.place_name)))
        .catch(() => {});
    load();
    window.addEventListener("wp:follows-changed", load);
    return () => window.removeEventListener("wp:follows-changed", load);
  }, [user]);

  useEffect(() => {
    setFeedsLoading(true);
    setFeeds(null);
    axios.get(`${API}/feeds/country/${encodeURIComponent(selected)}`)
      .then((r) => setFeeds(r.data))
      .catch(() => {})
      .finally(() => setFeedsLoading(false));
  }, [selected]);

  useEffect(() => {
    const h = (e) => {
      setSelected(e.detail);
      document.getElementById("worldmap")?.scrollIntoView({ behavior: "smooth" });
    };
    window.addEventListener("wp:select-country", h);
    return () => window.removeEventListener("wp:select-country", h);
  }, []);

  const isFollowing = follows.includes(selected);

  const toggleFollow = async () => {
    try {
      if (isFollowing) {
        await axios.delete(`${API}/follows/${encodeURIComponent(selected)}`, { headers: authHeaders() });
        toast.info(`${tr("Unfollowed")} ${selected}`);
      } else {
        await axios.post(`${API}/follows`, { place_name: selected, place_type: "country" }, { headers: authHeaders() });
        toast.success(`${tr("Following")} ${selected} — ${tr("see it in My World")}`);
      }
      window.dispatchEvent(new Event("wp:follows-changed"));
    } catch (_) {
      toast.error(tr("Please sign in first."));
    }
  };

  const setHome = async () => {
    try {
      await updatePreferences(selected);
      toast.success(`${selected} ${tr("saved as your home country.")}`);
    } catch (_) {
      toast.error(tr("Please sign in first."));
    }
  };

  useEffect(() => {
    if (!searchQuery) return;
    const tryMatch = (attempt = 0) => {
      const q = searchQuery.toLowerCase();
      const match =
        namesRef.current.find((n) => n.toLowerCase() === q) ||
        namesRef.current.find((n) => n.toLowerCase().includes(q));
      if (match) setSelected(match);
      else if (attempt < 10) setTimeout(() => tryMatch(attempt + 1), 500);
    };
    tryMatch();
    document.getElementById("worldmap")?.scrollIntoView({ behavior: "smooth" });
  }, [searchQuery, searchTick]);

  const premium = PREMIUM[selected];
  const chain = premium ? HIERARCHY_PATHS[premium.path]?.chain : null;
  const listenText = `${tr("The Interactive World Map")}. ${selected}. ${premium ? `${tr("Premium country portal")}: ${premium.domain}.` : tr("Served by the global WorldPulse engine.")}`;

  return (
    <section id="worldmap" data-testid="world-map-section" className="py-16 lg:py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-4 lg:px-8">
        <Reveal>
          <div className="flex items-center gap-4 flex-wrap">
            <h2 className="text-2xl md:text-3xl font-extrabold text-[#0F2557] tracking-tight">{tr("Tap a Country. Preview its Portal.")}</h2>
            <ListenButton compact text={listenText} />
          </div>
          <p className="text-[#64748B] mt-3 max-w-2xl">{tr("Every country on Earth is served by one geographic engine — tap any territory to see its portal path, domain and drill-down hierarchy.")}</p>
        </Reveal>

        <div className="grid lg:grid-cols-3 gap-8 mt-10 items-start">
          <div className="lg:col-span-2 border border-[#E5EDF7] rounded-3xl overflow-hidden bg-[#F6FAFF]" data-testid="world-map-canvas">
            <MapChart selected={selected} onSelect={setSelected} namesRef={namesRef} />
            <div className="flex items-center gap-5 px-5 py-3 border-t border-[#E5EDF7] text-xs font-semibold text-[#64748B] flex-wrap">
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#2563EB]" /> {tr("Selected")}</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#93C5FD]" /> {tr("Premium domain live")}</span>
              <span className="flex items-center gap-2"><span className="w-3 h-3 rounded-sm bg-[#DCE8F7]" /> {tr("Global engine")}</span>
              <span className="ml-auto hidden md:inline">{tr("Scroll to zoom · drag to pan")}</span>
            </div>
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={selected}
              data-testid="portal-path-panel"
              initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="bg-white border border-[#E5EDF7] rounded-3xl p-7 shadow-lg shadow-blue-50"
            >
              <div className="text-[0.62rem] font-extrabold tracking-widest text-[#2563EB] uppercase">{tr("Portal Path")}</div>
              <h3 data-testid="selected-country-name" className="text-2xl font-extrabold text-[#0F2557] mt-2">{selected}</h3>

              <div className="mt-5 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-mono-wp text-xs bg-[#F1F6FD] border border-[#DCE8F7] rounded-lg px-3 py-2 text-[#334155] truncate">
                    worldpulse.pro/country/{slug(selected)}
                  </span>
                </div>
                {premium ? (
                  <div className="flex items-center gap-2">
                    <ArrowRight size={15} className="text-[#2563EB] shrink-0" />
                    <span className="font-extrabold text-[#16A34A] text-sm">{premium.domain}</span>
                    <span className="text-[0.6rem] font-bold uppercase tracking-wider text-white bg-[#16A34A] rounded-full px-2.5 py-1">{tr("Premium domain live")}</span>
                  </div>
                ) : (
                  <div className="text-xs text-[#64748B] font-semibold">{tr("Served by the global WorldPulse engine — premium domain available for licensing.")}</div>
                )}
              </div>

              <div className="border-t border-[#EEF3FA] mt-6 pt-5">
                <div className="text-[0.62rem] font-extrabold tracking-widest text-[#64748B] uppercase mb-3">{tr("Drill-down preview")}</div>
                {chain ? (
                  <div className="space-y-1.5">
                    {chain.slice(1).map((c, i) => (
                      <div key={c.name} className="flex items-center gap-2 text-sm" style={{ paddingLeft: i * 14 }}>
                        <span className="text-[#2563EB]">↳</span>
                        <span className="font-bold text-[#0F2557]">{c.name}</span>
                        <span className="text-[0.6rem] font-mono-wp text-[#94A3B8]">{c.level}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-1.5 text-sm">
                    {[tr("States / Provinces"), tr("Counties / Districts"), tr("Cities & Towns"), tr("Villages & Localities")].map((l, i) => (
                      <div key={l} className="flex items-center gap-2" style={{ paddingLeft: i * 14 }}>
                        <span className="text-[#93C5FD]">↳</span>
                        <span className="font-semibold text-[#64748B]">{l}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Link
                data-testid="open-country-page-link"
                to={`/country/${slug(selected)}`}
                className="mt-3 block w-full text-center border-2 border-[#2563EB] text-[#2563EB] hover:bg-[#EAF2FD] font-bold text-sm py-2.5 rounded-xl transition-colors duration-200"
              >
                {tr("Open country home page")} →
              </Link>

              <div className="mt-5">
                <ShareBadge url={`https://worldpulse.pro/country/${slug(selected)}`} />
              </div>

              {user && (
                <div className="flex gap-2 mt-4">
                  <button
                    data-testid="follow-country-button"
                    onClick={toggleFollow}
                    className={`flex-1 flex items-center justify-center gap-2 text-xs font-bold py-2.5 rounded-xl border transition-all duration-200 active:scale-[0.97] ${
                      isFollowing ? "bg-[#FEE2E2] border-[#FCA5A5] text-[#DC2626]" : "bg-[#F0FDF4] border-[#86EFAC] text-[#15803D] hover:bg-[#DCFCE7]"
                    }`}
                  >
                    <Heart size={13} fill={isFollowing ? "currentColor" : "none"} /> {isFollowing ? tr("Unfollow") : tr("Follow")}
                  </button>
                  <button
                    data-testid="set-home-country-button"
                    onClick={setHome}
                    className={`flex-1 flex items-center justify-center gap-2 text-xs font-bold py-2.5 rounded-xl border transition-all duration-200 active:scale-[0.97] ${
                      user.preferred_country === selected ? "bg-[#EAF2FD] border-[#2563EB] text-[#1D4ED8]" : "bg-white border-[#BFD4F2] text-[#334155] hover:bg-[#EAF2FD]"
                    }`}
                  >
                    <Home size={13} /> {user.preferred_country === selected ? tr("Home country") : tr("Set as home")}
                  </button>
                </div>
              )}

              <button
                data-testid="claim-territory-button"
                onClick={() => document.getElementById("waitlist")?.scrollIntoView({ behavior: "smooth" })}
                className="mt-6 w-full bg-[#2563EB] hover:bg-[#1D4ED8] text-white font-bold text-sm py-3 rounded-xl transition-colors duration-200 active:scale-[0.98]"
              >
                {tr("Claim this territory")} →
              </button>
            </motion.div>
          </AnimatePresence>
        </div>

        <Reveal delay={0.1}>
          <div className="mt-8" data-testid="federation-feeds-panel">
            <div className="flex items-center gap-3 flex-wrap">
              <h3 className="font-extrabold text-[#0F2557]">{tr("Live Federation Feeds")} — {selected}</h3>
              {feedsLoading && <Loader2 size={15} className="animate-spin text-[#2563EB]" />}
            </div>
            {feeds && (
              <div className="grid md:grid-cols-3 gap-5 mt-4">
                <div data-testid="feed-weather-card" className="border border-[#FDBA74] rounded-3xl p-5 bg-[#FFF7ED]">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-bold text-sm text-[#9A3412]"><ThermometerSun size={16} /> {tr("Weather")}{feeds.capital ? ` · ${feeds.capital}` : ""}</span>
                    {feeds.weather?.live && <span className="text-[0.55rem] font-extrabold tracking-widest text-[#15803D] bg-[#F0FDF4] border border-[#C7E8D2] rounded-full px-2 py-0.5">LIVE</span>}
                  </div>
                  {feeds.weather ? (
                    <div className="mt-3">
                      <div className="text-3xl font-extrabold text-[#0F2557]">{Math.round(feeds.weather.temp)}°C</div>
                      <div className="text-sm text-[#334155] font-semibold">{feeds.weather.condition}</div>
                      <div className="text-xs text-[#64748B] mt-1">H {Math.round(feeds.weather.high)}° / L {Math.round(feeds.weather.low)}° · {tr("humidity")} {feeds.weather.humidity}% · {tr("wind")} {feeds.weather.wind} km/h</div>
                      <div className="text-[0.6rem] text-[#94A3B8] mt-2">{tr("Source")}: {feeds.weather.source}</div>
                    </div>
                  ) : (
                    <p className="text-xs text-[#64748B] mt-3">{tr("Weather unavailable for this territory right now.")}</p>
                  )}
                </div>
                <div data-testid="feed-aviation-card" className="border border-[#93C5FD] rounded-3xl p-5 bg-[#F6FAFF]">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-bold text-sm text-[#1D4ED8]"><Plane size={16} /> {tr("Departures")}</span>
                    <span className="text-[0.55rem] font-extrabold tracking-widest text-[#92400E] bg-[#FFF7ED] border border-[#FDBA74] rounded-full px-2 py-0.5">{tr("DEMO")}</span>
                  </div>
                  <div className="mt-3 space-y-2">
                    {feeds.aviation.items.map((a) => (
                      <div key={a.flight} className="flex justify-between text-xs font-mono-wp text-[#334155]">
                        <span className="font-bold">{a.flight}</span>
                        <span className="truncate px-2 flex-1">{a.route}</span>
                        <span>{a.time}</span>
                        <span className={`ml-2 font-bold ${a.status === "Delayed" ? "text-[#DC2626]" : "text-[#15803D]"}`}>{a.status}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div data-testid="feed-civic-card" className="border border-[#D8CCF2] rounded-3xl p-5 bg-[#FAF5FF]">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-2 font-bold text-sm text-[#6D28D9]"><Landmark size={16} /> {tr("Civic Notices")}</span>
                    <span className="text-[0.55rem] font-extrabold tracking-widest text-[#92400E] bg-[#FFF7ED] border border-[#FDBA74] rounded-full px-2 py-0.5">{tr("DEMO")}</span>
                  </div>
                  <div className="mt-3 space-y-2.5">
                    {feeds.civic.items.map((c) => (
                      <div key={c.title} className="text-xs text-[#334155]">
                        <span className="font-bold">{c.title}</span> — {c.body}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </Reveal>

        <LinkIntelligence />
      </div>
    </section>
  );
}
